import { benchSeatTopMm } from "../../domain/interior";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import {
	formatLengthM,
	formatLengthMm,
	type UnitSystem,
} from "../../domain/units/formatUnits";
import type { BuildInput } from "../../engines/types";
import type { Dictionary } from "../../i18n";
import { applyArgs } from "../../i18n/buildGuide";
import { getMarketProfile } from "../../market/marketProfile";
import type { InteriorDragPreview } from "./InteriorDragController";
import {
	createBenchEdgeMeasurementTarget,
	createBenchMeasurementTarget,
	createHeaterMeasurementTarget,
	getWallLengthMm,
	type MeasurementTarget,
} from "./ViewerMeasurementController";

const WALL_BASE_Y = 0.165;
const WALL_PLANE_OFFSET = 0.09;
const WALL_GUIDE_OFFSET = 0.012;
const OPENING_GUIDE_LIFT = 0.2;

export interface MeasurementMetric {
	label: string;
	value: string;
}

export interface MeasurementSceneBadge {
	id: string;
	label: string;
	position: [number, number, number];
}

export interface MeasurementSceneGuide {
	id: string;
	start: [number, number, number];
	end: [number, number, number];
	label: string;
	labelPosition: [number, number, number];
	tickAxis?: "y" | "x" | "z";
	emphasize?: boolean;
}

export interface PartitionMeasurementPreview {
	partitionSplit: number;
	saunaDepthMm: number;
	loungeDepthMm: number;
	dividerZ: number;
	valid: boolean;
}

export interface MeasurementReadout {
	title: string;
	subtitle: string;
	metrics: MeasurementMetric[];
	badges: MeasurementSceneBadge[];
	guides: MeasurementSceneGuide[];
	/**
	 * One line under the metrics: what the live gesture would do, or why it would
	 * be refused. A row in this card rather than its own floating overlay — the
	 * viewer's bottom band is already shared by the help panel and this card.
	 */
	note?: { text: string; invalid?: boolean };
}

/** Compile-time exhaustiveness: an unhandled `MeasurementTarget` kind fails tsc. */
function assertNever(value: never): null {
	void value;
	return null;
}

function getLocale(buildInput: BuildInput): "sv-SE" | "en-US" {
	return getMarketProfile(buildInput.market ?? "se").locale;
}

function formatMeters(
	value: number,
	unitSystem: UnitSystem,
	locale: "sv-SE" | "en-US",
) {
	return formatLengthM(value, unitSystem, locale);
}

function formatMillimeters(
	value: number,
	unitSystem: UnitSystem,
	locale: "sv-SE" | "en-US",
) {
	return formatLengthMm(value, unitSystem, locale);
}

function wallTitle(
	wall: "front" | "back" | "left" | "right" | "divider",
	t: Dictionary,
) {
	switch (wall) {
		case "front":
			return t.viewer_measurementWallFront;
		case "back":
			return t.viewer_measurementWallBack;
		case "left":
			return t.viewer_measurementWallLeft;
		case "right":
			return t.viewer_measurementWallRight;
		case "divider":
			return t.viewer_measurementPartitionTitle;
	}
}

function getWallLengthMeters(
	buildInput: BuildInput,
	wall: "front" | "back" | "left" | "right" | "divider",
) {
	return getWallLengthMm(wall, buildInput) / 1000;
}

function getOpeningById(buildInput: BuildInput, openingId: string) {
	return (
		buildInput.openings.find((opening) => opening.id === openingId) ?? null
	);
}

function getOpeningLabels(opening: WallOpening, t: Dictionary) {
	if (opening.wall === "left" || opening.wall === "right") {
		return {
			start: t.viewer_measurementBackOffset,
			end: t.viewer_measurementFrontOffset,
		};
	}

	return {
		start: t.viewer_measurementLeftOffset,
		end: t.viewer_measurementRightOffset,
	};
}

function makeWallGuides(
	buildInput: BuildInput,
	wall: "front" | "back" | "left" | "right" | "divider",
	label: string,
): MeasurementSceneGuide[] {
	const { width, length } = buildInput.dimensions;
	const y = WALL_BASE_Y + resolveWallHeightM(buildInput.wallHeightM) / 2;
	const labelY = y;
	const dividerZ = -length / 2 + length * (buildInput.partitionSplit ?? 0.5);

	switch (wall) {
		case "front": {
			const z = length / 2 - WALL_PLANE_OFFSET + WALL_GUIDE_OFFSET;
			return [
				{
					id: "wall-front",
					start: [-width / 2, y, z],
					end: [width / 2, y, z],
					label,
					labelPosition: [0, labelY, z],
					tickAxis: "y",
					emphasize: true,
				},
			];
		}
		case "back": {
			const z = -length / 2 + WALL_PLANE_OFFSET - WALL_GUIDE_OFFSET;
			return [
				{
					id: "wall-back",
					start: [-width / 2, y, z],
					end: [width / 2, y, z],
					label,
					labelPosition: [0, labelY, z],
					tickAxis: "y",
					emphasize: true,
				},
			];
		}
		case "left": {
			const x = -width / 2 + WALL_PLANE_OFFSET - WALL_GUIDE_OFFSET;
			return [
				{
					id: "wall-left",
					start: [x, y, -length / 2],
					end: [x, y, length / 2],
					label,
					labelPosition: [x, labelY, 0],
					tickAxis: "y",
					emphasize: true,
				},
			];
		}
		case "right": {
			const x = width / 2 - WALL_PLANE_OFFSET + WALL_GUIDE_OFFSET;
			return [
				{
					id: "wall-right",
					start: [x, y, -length / 2],
					end: [x, y, length / 2],
					label,
					labelPosition: [x, labelY, 0],
					tickAxis: "y",
					emphasize: true,
				},
			];
		}
		case "divider": {
			const z = dividerZ - 0.01;
			const halfSpan = Math.max(0, width / 2 - 0.095);
			return [
				{
					id: "wall-divider",
					start: [-halfSpan, y, z],
					end: [halfSpan, y, z],
					label,
					labelPosition: [0, labelY, z],
					tickAxis: "y",
					emphasize: true,
				},
			];
		}
	}
}

function makeOpeningGuides(
	buildInput: BuildInput,
	opening: WallOpening,
	unitSystem: UnitSystem,
	locale: "sv-SE" | "en-US",
): MeasurementSceneGuide[] {
	const widthMm = opening.widthMm;
	const openingStartMm = opening.xMm;
	const wallLengthMm = getWallLengthMm(opening.wall, buildInput);
	const openingEndMm = openingStartMm + widthMm;
	const remainingMm = Math.max(0, wallLengthMm - openingEndMm);
	const openingStartM = opening.xMm / 1000;
	const openingWidthM = widthMm / 1000;
	const y =
		WALL_BASE_Y +
		opening.yMm / 1000 +
		opening.heightMm / 1000 +
		OPENING_GUIDE_LIFT;
	const labelY = y + 0.08;
	const { width, length } = buildInput.dimensions;
	const dividerZ = -length / 2 + length * (buildInput.partitionSplit ?? 0.5);

	const makeGuide = (
		id: string,
		start: [number, number, number],
		end: [number, number, number],
		label: string,
		labelPosition: [number, number, number],
	): MeasurementSceneGuide => ({
		id,
		start,
		end,
		label,
		labelPosition,
		tickAxis: "y",
	});

	if (
		opening.wall === "front" ||
		opening.wall === "back" ||
		opening.wall === "divider"
	) {
		const planeZ =
			opening.wall === "front"
				? length / 2 - WALL_PLANE_OFFSET
				: opening.wall === "back"
					? -length / 2 + WALL_PLANE_OFFSET
					: dividerZ - 0.01;
		const wallStartX = -width / 2;
		const openingStartX = wallStartX + openingStartM;
		const openingEndX = openingStartX + openingWidthM;
		const wallEndX = width / 2;

		return [
			makeGuide(
				`${opening.id}-guide-start`,
				[wallStartX, y, planeZ],
				[openingStartX, y, planeZ],
				formatMillimeters(opening.xMm, unitSystem, locale),
				[(wallStartX + openingStartX) / 2, labelY, planeZ],
			),
			makeGuide(
				`${opening.id}-guide-width`,
				[openingStartX, y, planeZ],
				[openingEndX, y, planeZ],
				formatMillimeters(widthMm, unitSystem, locale),
				[(openingStartX + openingEndX) / 2, labelY, planeZ],
			),
			makeGuide(
				`${opening.id}-guide-end`,
				[openingEndX, y, planeZ],
				[wallEndX, y, planeZ],
				formatMillimeters(remainingMm, unitSystem, locale),
				[(openingEndX + wallEndX) / 2, labelY, planeZ],
			),
		];
	}

	const planeX =
		opening.wall === "left"
			? -width / 2 + WALL_PLANE_OFFSET
			: width / 2 - WALL_PLANE_OFFSET;
	const wallStartZ = -length / 2;
	const openingStartZ = wallStartZ + openingStartM;
	const openingEndZ = openingStartZ + openingWidthM;
	const wallEndZ = length / 2;

	return [
		makeGuide(
			`${opening.id}-guide-start`,
			[planeX, y, wallStartZ],
			[planeX, y, openingStartZ],
			formatMillimeters(opening.xMm, unitSystem, locale),
			[planeX, labelY, (wallStartZ + openingStartZ) / 2],
		),
		makeGuide(
			`${opening.id}-guide-width`,
			[planeX, y, openingStartZ],
			[planeX, y, openingEndZ],
			formatMillimeters(widthMm, unitSystem, locale),
			[planeX, labelY, (openingStartZ + openingEndZ) / 2],
		),
		makeGuide(
			`${opening.id}-guide-end`,
			[planeX, y, openingEndZ],
			[planeX, y, wallEndZ],
			formatMillimeters(remainingMm, unitSystem, locale),
			[planeX, labelY, (openingEndZ + wallEndZ) / 2],
		),
	];
}

function makePartitionBadges(
	buildInput: BuildInput,
	unitSystem: UnitSystem,
	locale: "sv-SE" | "en-US",
): MeasurementSceneBadge[] {
	const { width, length } = buildInput.dimensions;
	const split = buildInput.partitionSplit ?? 0.5;
	const dividerZ = -length / 2 + length * split;
	const frontDistance = length * (1 - split);
	const backDistance = length * split;
	const y = WALL_BASE_Y + resolveWallHeightM(buildInput.wallHeightM) + 0.08;

	return [
		{
			id: "partition-front",
			label: formatMeters(frontDistance, unitSystem, locale),
			position: [0, y, dividerZ + frontDistance / 2],
		},
		{
			id: "partition-back",
			label: formatMeters(backDistance, unitSystem, locale),
			position: [0, y, dividerZ - backDistance / 2],
		},
		{
			id: "partition-width",
			label: formatMeters(width, unitSystem, locale),
			position: [0, WALL_BASE_Y + 1.2, dividerZ],
		},
	];
}

function makePartitionGuides(
	buildInput: BuildInput,
	unitSystem: UnitSystem,
	locale: "sv-SE" | "en-US",
	preview?: PartitionMeasurementPreview,
): MeasurementSceneGuide[] {
	const length = buildInput.dimensions.length;
	const dividerZ =
		preview?.dividerZ ??
		-length / 2 + length * (buildInput.partitionSplit ?? 0.5);
	const backWallZ = -length / 2 + WALL_PLANE_OFFSET;
	const frontWallZ = length / 2 - WALL_PLANE_OFFSET;
	const guideY = WALL_BASE_Y + 1.1;
	const labelY = guideY + 0.08;
	const backDistanceM = preview
		? preview.saunaDepthMm / 1000
		: (buildInput.partitionSplit ?? 0.5) * length;
	const frontDistanceM = preview
		? preview.loungeDepthMm / 1000
		: length - backDistanceM;

	return [
		{
			id: "partition-guide-back",
			start: [0, guideY, dividerZ],
			end: [0, guideY, backWallZ],
			label: formatMeters(backDistanceM, unitSystem, locale),
			labelPosition: [0, labelY, (dividerZ + backWallZ) / 2],
			tickAxis: "y",
		},
		{
			id: "partition-guide-front",
			start: [0, guideY, dividerZ],
			end: [0, guideY, frontWallZ],
			label: formatMeters(frontDistanceM, unitSystem, locale),
			labelPosition: [0, labelY, (dividerZ + frontWallZ) / 2],
			tickAxis: "y",
		},
	];
}

export function resolveMeasurementReadout(
	target: MeasurementTarget | null,
	buildInput: BuildInput,
	t: Dictionary,
	unitSystem: UnitSystem = "metric",
): MeasurementReadout | null {
	if (!target) {
		return null;
	}

	const locale = getLocale(buildInput);

	if (target.kind === "wall") {
		const wallLength = getWallLengthMeters(buildInput, target.wall);
		return {
			title:
				target.wall === "divider"
					? t.viewer_measurementPartitionTitle
					: t.viewer_measurementWallTitle,
			subtitle: wallTitle(target.wall, t),
			metrics: [
				{
					label: t.viewer_measurementLength,
					value: formatMeters(wallLength, unitSystem, locale),
				},
			],
			badges: [],
			guides: makeWallGuides(
				buildInput,
				target.wall,
				formatMeters(wallLength, unitSystem, locale),
			),
		};
	}

	if (target.kind === "opening") {
		const opening = getOpeningById(buildInput, target.openingId);
		if (!opening) {
			return null;
		}

		const wallLengthMm = getWallLengthMm(opening.wall, buildInput);
		const openingEndMm = opening.xMm + opening.widthMm;
		const remainingMm = Math.max(0, wallLengthMm - openingEndMm);
		const labels = getOpeningLabels(opening, t);

		return {
			title:
				opening.type === "door"
					? t.viewer_measurementDoorTitle
					: t.viewer_measurementWindowTitle,
			subtitle: wallTitle(
				opening.wall === "divider" ? "front" : opening.wall,
				t,
			),
			metrics: [
				{
					label: t.viewer_measurementWidth,
					value: formatMillimeters(opening.widthMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementHeight,
					value: formatMillimeters(opening.heightMm, unitSystem, locale),
				},
				{
					label: labels.start,
					value: formatMillimeters(opening.xMm, unitSystem, locale),
				},
				{
					label: labels.end,
					value: formatMillimeters(remainingMm, unitSystem, locale),
				},
			],
			badges: [],
			guides: makeOpeningGuides(buildInput, opening, unitSystem, locale),
		};
	}

	// Lavar and the aggregat (issue 133).
	//
	// Each target carries its own millimetres, so nothing here reads the layout
	// back: a readout that re-derived the run would be a third description of the
	// same bench beside the model's and the BOM's.
	//
	// These branches are also what stops the new kinds falling through to the
	// partition case below. That fall-through has no `kind` guard, so an unhandled
	// target would have quietly rendered partition depths under a bench's name.
	// A lav's body and one of its ends describe the SAME run, so they share one
	// readout — only the subtitle says which end you have hold of. Two copies had
	// already drifted to different metric counts.
	if (target.kind === "bench" || target.kind === "bench-edge") {
		const tierLabel =
			target.tier === "upper" ? t.plan_view_benchUpper : t.plan_view_benchLower;
		return {
			title: t.viewer_measurementBenchTitle,
			subtitle:
				target.kind === "bench-edge"
					? `${tierLabel} — ${
							target.edge === "start"
								? t.viewer_measurementBenchStartEdge
								: t.viewer_measurementBenchEndEdge
						}`
					: `${tierLabel} — ${wallTitle(target.wall, t)}`,
			metrics: [
				{
					label: t.viewer_measurementLength,
					value: formatMillimeters(target.lengthMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementDepth,
					value: formatMillimeters(target.depthMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementAlongWall,
					value: formatMillimeters(target.startMm, unitSystem, locale),
				},
				...(target.kind === "bench" && target.seatHeightMm !== undefined
					? [
							{
								label: t.interior_seatHeight,
								value: formatMillimeters(
									target.seatHeightMm,
									unitSystem,
									locale,
								),
							},
						]
					: []),
			],
			badges: [],
			guides: [],
		};
	}

	if (target.kind === "heater") {
		return {
			title: t.viewer_measurementHeaterTitle,
			subtitle: wallTitle(target.wall, t),
			metrics: [
				{
					label: t.viewer_measurementWidth,
					value: formatMillimeters(target.widthMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementDepth,
					value: formatMillimeters(target.depthMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementAlongWall,
					value: formatMillimeters(target.offsetMm, unitSystem, locale),
				},
				{
					label: t.viewer_measurementWallGap,
					value: formatMillimeters(target.wallGapMm, unitSystem, locale),
				},
			],
			badges: [],
			guides: [],
		};
	}

	// Explicit, not a fall-through. With three kinds the default was tolerable; at
	// six it is a trap — an unhandled kind rendered lounge depths under a bench's
	// name, which is exactly the silent-wrong-number class this repo keeps burning
	// on. `assertNever` turns kind number seven into a `tsc` error instead.
	if (target.kind !== "partition") return assertNever(target);

	const split = buildInput.partitionSplit ?? 0.5;
	const saunaDepth = buildInput.dimensions.length * split;
	const loungeDepth = buildInput.dimensions.length - saunaDepth;

	return {
		title: t.viewer_measurementPartitionTitle,
		subtitle: t.viewer_measurementPartitionSubtitle,
		metrics: [
			{
				label: t.viewer_measurementBackDistance,
				value: formatMeters(saunaDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementFrontDistance,
				value: formatMeters(loungeDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementSaunaDepth,
				value: formatMeters(saunaDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementLoungeDepth,
				value: formatMeters(loungeDepth, unitSystem, locale),
			},
		],
		badges: makePartitionBadges(buildInput, unitSystem, locale),
		guides: [],
	};
}

export function resolvePartitionPreviewReadout(
	preview: PartitionMeasurementPreview,
	buildInput: BuildInput,
	t: Dictionary,
	unitSystem: UnitSystem = "metric",
): MeasurementReadout {
	const locale = getLocale(buildInput);
	const saunaDepth = preview.saunaDepthMm / 1000;
	const loungeDepth = preview.loungeDepthMm / 1000;

	return {
		title: t.viewer_measurementPartitionTitle,
		subtitle: t.viewer_measurementPartitionSubtitle,
		metrics: [
			{
				label: t.viewer_measurementBackDistance,
				value: formatMeters(saunaDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementFrontDistance,
				value: formatMeters(loungeDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementSaunaDepth,
				value: formatMeters(saunaDepth, unitSystem, locale),
			},
			{
				label: t.viewer_measurementLoungeDepth,
				value: formatMeters(loungeDepth, unitSystem, locale),
			},
		],
		badges: [],
		guides: makePartitionGuides(buildInput, unitSystem, locale, preview),
	};
}

/**
 * The live readout for an interior drag (issue 133).
 *
 * Built from the CANDIDATE's own measurement target, then handed to
 * `resolveMeasurementReadout` — the same function a hover uses. That is the whole
 * point: the card must not change shape when the user starts dragging. Two authors
 * had already diverged (dragging the heater dropped width and depth; dragging a
 * bench END gained two metrics a hover on it did not show), which is exactly the
 * kind of drift a shared function cannot have.
 *
 * The refusal text is the DOMAIN's: `validateInteriorLayout` returns a translation
 * key plus its own args, substituted by the shared `applyArgs`. The viewer authors
 * no rule wording, so the two editing surfaces cannot explain one refusal in two
 * different ways.
 */
export function resolveInteriorDragReadout(
	preview: InteriorDragPreview,
	buildInput: BuildInput,
	t: Dictionary,
	unitSystem: UnitSystem = "metric",
): MeasurementReadout | null {
	const target = interiorPreviewTarget(preview, buildInput);
	if (!target) return null;
	const readout = resolveMeasurementReadout(target, buildInput, t, unitSystem);
	if (!readout) return null;

	// A note only when the drag would be REFUSED. A hint rendered mid-gesture tells
	// the user how to do the thing they are already doing, and rendering it red with
	// role="alert" on an invalid drop made it look like an explanation it wasn't.
	if (preview.valid || !preview.issue) return readout;
	return {
		...readout,
		note: {
			text: applyArgs(t[preview.issue.messageKey], preview.issue.args),
			invalid: true,
		},
	};
}

/** The candidate's own handle target, built by the factories that own the ids. */
function interiorPreviewTarget(
	preview: InteriorDragPreview,
	buildInput: BuildInput,
): MeasurementTarget | null {
	const { target } = preview;
	if (target.kind === "heater") {
		return createHeaterMeasurementTarget(
			preview.layout.heater,
			preview.heaterSizeMm,
		);
	}
	const run = preview.layout.benchRuns.find(
		(entry) => entry.id === target.runId,
	);
	// Null when reconciliation dropped the run mid-gesture; the card hides rather
	// than describing a bench that no longer exists.
	if (!run) return null;
	return target.kind === "bench-edge"
		? createBenchEdgeMeasurementTarget(run, target.edge)
		: target.kind === "bench-height"
			? {
					...createBenchMeasurementTarget(run),
					seatHeightMm: benchSeatTopMm(
						run,
						resolveWallHeightM(buildInput.wallHeightM) * 1000,
					),
				}
			: createBenchMeasurementTarget(run);
}
